import { useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  useBoardPermissions,
  useBoards,
  useUserBoardPermissions,
} from "../../data/queries/boardQueries";
import { BoardContext } from "./boardContext";
import { IconLoading } from "../../components/Loading/IconLoading";

export const BoardProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: userBoardPermissions,
    isPending: isUserBoardPermissionsPending,
    isFetching: isUserBoardPermissionsFetching,
    isSuccess: isUserBoardPermissionsSuccess,
  } = useUserBoardPermissions();

  const {
    data: boards,
    isPending: isBoardPending,
    isSuccess: isBoardSucess,
  } = useBoards();

  const firstBoardWithPermissions = useMemo(() => {
    if (userBoardPermissions && boards) {
      return boards.find((board) =>
        userBoardPermissions.some(
          (permission) => permission.BoardId === board.Id
        )
      );
    }

    return null;
  }, [userBoardPermissions, boards]);

  const fallbackBoard = firstBoardWithPermissions?.Id ?? null;

  const boardId = searchParams.get("boardId") || fallbackBoard;

  const { data: boardPermissions, isLoading: isBoardPermissionsLoading } =
    useBoardPermissions(boardId);

  const currentBoard = useMemo(() => {
    if (boards && boardId) {
      return boards.find((board) => board.Id === boardId);
    }

    return null;
  }, [boards, boardId]);

  const value = useMemo(
    () => ({
      currentBoardId: boardId,
      currentBoard,
      boards,
      unarchivedBoards: boards?.filter((board) => !board.Archived),
      archivedBoards: boards?.filter((board) => board.Archived),
      boardPermissions,
    }),
    [boardId, currentBoard, boards, boardPermissions]
  );

  if (
    (isBoardPending && !isBoardSucess) ||
    isBoardPermissionsLoading ||
    ((isUserBoardPermissionsPending || isUserBoardPermissionsFetching) &&
      !isUserBoardPermissionsSuccess)
  ) {
    return <IconLoading />;
  }

  // NOTE(ivelez): prevents from going back to invalid-board-access page after an Admin adds themselves to the board's permissions
  if (isUserBoardPermissionsFetching && !isBoardSucess) return;

  if (isBoardSucess) {
    // wait for boards to be fetched
    let defaultBoardId = boardId;

    if (fallbackBoard && !boardId) {
      defaultBoardId = fallbackBoard;
    }

    const params = new URLSearchParams(window.location.search);
    const currentBoardId = params.get("boardId");

    const userHasAccessToCurrentBoard = userBoardPermissions?.find(
      (x) => x.BoardId === currentBoardId
    );

    if (!firstBoardWithPermissions) {
      // when user has no board permissions.
      sessionStorage.removeItem("boardId");
      return (
        <Navigate
          to="/duedates/invalid-board-access"
          state={{ boardRequestingAccessFor: null }}
        />
      );
    } else if (
      currentBoardId &&
      userBoardPermissions &&
      !userHasAccessToCurrentBoard
    ) {
      // when a user has board permissions, but not for the desired board selected.
      sessionStorage.removeItem("boardId");
      return (
        <Navigate
          to="/duedates/invalid-board-access"
          state={{ boardRequestingAccessFor: currentBoard }}
        />
      );
    } else if (!currentBoardId && defaultBoardId) {
      setTimeout(() => {
        setSearchParams({ boardId: defaultBoardId });
      }, 0);
      sessionStorage.removeItem("boardId");
    }
  }

  return (
    <BoardContext.Provider value={value}>{children}</BoardContext.Provider>
  );
};
